import { Const } from "../core/constants";

export interface KarelImages {
  karel?: HTMLImageElement;
  karelArrow?: HTMLImageElement;
  haveCalledLoadImages(): boolean;
  haveLoadedAllImages(): boolean;
  addListener(listener: () => void): void;
  removeListener(listener: () => void): void;
  loadImages(callbackFn?: () => void): void;
  recolor(): Promise<void>;
}

// A single Karel robot and a single direction arrow, both drawn facing East.
// KarelView rotates them for the other three directions, so there is no need
// for per-direction (or per-size) sprites. Beepers are drawn with canvas
// primitives, so they have no sprite here.
const IMAGE_NAMES = ["karel.svg", "karelArrow.svg"];

// The bundled sprites use a fixed black outline/feet + white body. Those
// literals are swapped for the active theme's canvas palette so the robot
// stays legible on a dark playfield, where black would vanish. The source
// files remain the single source of truth for the artwork.
function applyThemeColors(svg: string): string {
  return svg
    .replace(/#000000/gi, Const.CANVAS_KAREL_STROKE)
    .replace(/#ffffff/gi, Const.CANVAS_KAREL_FILL);
}

function toDataUri(svg: string): string {
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

// Resolves once the sprite can actually be drawn. `complete` is true for
// broken images too, so require a real intrinsic size before resolving.
function waitForLoad(image: HTMLImageElement): Promise<HTMLImageElement> {
  if (image.complete && image.naturalWidth > 0) return Promise.resolve(image);
  return new Promise((resolve, reject) => {
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load Karel sprite"));
  });
}

function loadThemedImage(name: string): Promise<HTMLImageElement> {
  return fetch("/images/" + name)
    .then((response) => response.text())
    .then((svg) => {
      const image = new Image();
      image.src = toDataUri(applyThemeColors(svg));
      return waitForLoad(image);
    });
}

export function createKarelImages(): KarelImages {
  const images: HTMLImageElement[] = [];
  let callback: (() => void) | null = null;
  let allLoaded = false;
  let calledLoadImages = false;
  const listeners: (() => void)[] = [];

  const that: KarelImages = {
    haveCalledLoadImages: () => calledLoadImages,
    haveLoadedAllImages: () => allLoaded,

    addListener(listener: () => void): void {
      listeners.push(listener);
    },

    removeListener(listener: () => void): void {
      const index = listeners.indexOf(listener);
      if (index !== -1) listeners.splice(index, 1);
    },

    loadImages(callbackFn?: () => void): void {
      if (calledLoadImages) alert("called loadImages twice");
      callback = callbackFn ?? null;
      calledLoadImages = true;

      void Promise.all(IMAGE_NAMES.map(loadThemedImage))
        .then((loaded) => {
          for (let i = 0; i < loaded.length; i++) images[i] = loaded[i];
          that.karel = images[0];
          that.karelArrow = images[1];
          allAreLoaded();
        })
        .catch((error) => {
          console.error("Failed to load Karel sprites", error);
        });
    },

    async recolor(): Promise<void> {
      // Before the first load, `loadImages` already picks up the current theme
      // colors, so there is nothing to regenerate.
      if (!allLoaded) return;
      try {
        const loaded = await Promise.all(IMAGE_NAMES.map(loadThemedImage));
        for (let i = 0; i < loaded.length; i++) images[i] = loaded[i];
        that.karel = images[0];
        that.karelArrow = images[1];
        window.dispatchEvent(new Event("karel:images-updated"));
      } catch (error) {
        // Keep the existing sprites rather than leaving the canvas blank.
        console.error("Failed to recolor Karel sprites", error);
      }
    },
  };

  function allAreLoaded(): void {
    allLoaded = true;
    if (callback) callback();
    for (let i = 0; i < listeners.length; i++) listeners[i]();
  }

  return that;
}

export const karelImages = createKarelImages();
